import {Button, Divider, Paper} from '@material-ui/core';
import 'Core/components/Dashboard/Dashboard.sass';
import {updatefeatureVariables} from 'Core/ducks/featureToggle';
import * as O from 'fp-ts/lib/Option';
import * as I from 'immutable';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useRouteMatch} from 'react-router';
import {useHistory} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import Alert from 'Shared/components/Alert';
import Loading from 'Shared/components/Loading';
import {isDeepEqual} from 'Shared/utilities/deepEqual';
import BasicUserInformationEntry from 'Users/components/UserAdministration/UserEntry/BasicUserInformationEntry';
import LineOfBusinessSelect from 'Users/components/UserAdministration/UserEntry/LineOfBusinessSelect';
import UserTypeEntry from 'Users/components/UserAdministration/UserEntry/UserTypeEntry';
import {addAccessFromLineOfBusiness, addUser, clearState, getUserDetail, updateUser} from 'Users/ducks/userEntry';
import {isExternalBasicInformation} from 'Users/models/User/BasicInformation';
import {isLoading, isUserWithErrors, isValid, ValidUser} from 'Users/models/User/User';
import {matchUserTypeByLineOfBusiness, UserType} from 'Users/models/User/UserType';
import './UserManualEntry.sass';
import { FeatureConstants, getOrCreateFeatureFlag } from 'Core/ducks/featureFlag';

const getUserTypeKey = (userTypeValue: UserType['value']) =>
  matchUserTypeByLineOfBusiness(userTypeValue, {
    Agent: () => 'agent-user-type',
    Quoting: () => 'quoting-user-type'
  });

const UserManualEntry = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const match = useRouteMatch<{userId: string}>();
  const userId = O.fromNullable(match.params.userId);
  const user = useSelector((state: ReducerState) => state.user.userEntry);
  const userInEditing = useSelector((state: ReducerState) => state.user.userEntryEdit.user);
  const {featureVariables} = useSelector((state: ReducerState) => state.featureToggle);
  const userTypes: I.List<UserType> = user.userTypes;

  const operationLabel = O.fold(
    () => 'Add',
    () => 'Edit'
  )(userId);

  useEffect(() => {
    dispatch(updatefeatureVariables({...featureVariables, FOOTER_OPEN_DEFAULT: false}));
    dispatch(getOrCreateFeatureFlag(FeatureConstants.BR_QUOTING_TOOL))
  }, []);

  useEffect(() => {
    if (O.isSome(userId)) {
      dispatch(getUserDetail(userId.value));
    }

    return () => {
      dispatch(clearState());
    };
  }, [dispatch]);

  const isEdit = O.isSome(userId);
  const userIdValue = O.isSome(userId) ? userId.value : '';

  const userTypeComparator = (x: UserType, y: UserType) => x.value.key.localeCompare(y.value.key);
  const hasBasicInfoChanged = !isDeepEqual(user.basicInformation, userInEditing.basicInformation);
  // Compare the userTypes separate to ignore order
  const hasUserTypesChanged = !isDeepEqual(
    user.userTypes.toArray().sort(userTypeComparator),
    userInEditing.userTypes.toArray().sort(userTypeComparator)
  );
  const hasValueChanged = hasBasicInfoChanged || hasUserTypesChanged;

  return isLoading(user) ? (
    <Loading />
  ) : (
    <Paper className="user-entry">
      {isUserWithErrors(user) && (
        <Alert message="An error has occurred while processing your request. We apologize for the inconvenience. If you need immediate assistance, please contact production support (866) 828-8074 or try your request later." />
      )}
      <h1 className="user-entry__title">{`${operationLabel} MyAccess User`}</h1>
      <form className="user-entry__form">
        <BasicUserInformationEntry basicUserInformation={user.basicInformation} isEdit={isEdit} />
        <Divider className="form__divider" />
        <p className="user-entry__user-access-permissions-header">User Access Permissions</p>
        <p className="user-entry__user-access-permissions-description">
          Please select line(s) of business below to grant this user access to different areas of the application.
        </p>
        {userTypes.map(userType => (
          <UserTypeEntry
            userType={userType}
            key={getUserTypeKey(userType.value)}
            isEdit={isEdit}
            isExternal={isExternalBasicInformation(user.basicInformation)}
            isUserSupport={user.basicInformation.isUserSupport}
          />
        ))}
        <LineOfBusinessSelect
          user={user}
          onSelectLineOfBusiness={lineOfBusiness => dispatch(addAccessFromLineOfBusiness(lineOfBusiness))}
          isExternal={isExternalBasicInformation(user.basicInformation)}
        />
        <div className="form__actions">
          <Button
            className="actions__cancel-button"
            variant="contained"
            onClick={() => {
              history.goBack();
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={!isValid(user) || (!hasValueChanged && isEdit)}
            color="primary"
            variant="contained"
            onClick={() =>
              isEdit ? dispatch(updateUser(user as ValidUser, userIdValue)) : dispatch(addUser(user as ValidUser))
            }
          >
            Save
          </Button>
        </div>
      </form>
    </Paper>
  );
};

export default UserManualEntry;
